import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Shield, Lock } from 'lucide-react';
import { authAPI } from '@/lib/api';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authAPI.login({ 
        username: formData.username, 
        password: formData.password 
      });
      
      // Verify this is an admin account
      if (response.data.user.role !== 'admin') {
        toast.error('Access denied. Admin credentials required.');
        setLoading(false);
        return;
      }
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Admin login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-slate-800 border-slate-700">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-ocean-deep rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 data-testid="admin-login-heading" className="text-2xl font-chivo font-bold text-white mb-2">
            Admin Access
          </h1>
          <p className="text-slate-400 text-sm">Authorized personnel only</p>
        </div>

        <form data-testid="admin-login-form" onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="username" className="text-slate-300">Username</Label>
            <Input
              data-testid="admin-username-input"
              id="username"
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              placeholder="Enter admin username"
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <button
                type="button"
                onClick={() => navigate('/admin/forgot-password')}
                className="text-xs text-ocean-turquoise hover:text-ocean-light transition-colors"
              >
                Forgot Password?
              </button>
            </div>
            <Input
              data-testid="admin-password-input"
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              placeholder="Enter password"
              required
            />
          </div>

          <Button
            data-testid="admin-login-submit-btn"
            type="submit"
            className="w-full bg-ocean-deep hover:bg-ocean"
            disabled={loading}
          >
            {loading ? (
              'Authenticating...'
            ) : (
              <>
                <Lock className="w-4 h-4 mr-2" />
                Access Dashboard
              </>
            )}
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t border-slate-700">
          <p className="text-xs text-slate-500 text-center">
            This is a restricted area. Unauthorized access attempts are logged.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default AdminLogin;
